import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import '../components/ui/ui-button';
import { navigateBack } from '../router';
import { cardStyles, motionStyles, pageFrameStyles } from '../styles/lit-shared';

const SKILLS = [
  { icon: '🎵', title: 'Consistencia de tono', desc: 'Comprueba que el artículo suena igual de principio a fin. Si hay una sección que chirría, la detecta.' },
  { icon: '📏', title: 'Control de extensión', desc: 'Si el artículo se ha quedado corto, lo expande él mismo antes de entregarlo. No lo devuelve al Writer, lo arregla en el momento.' },
  { icon: '📋', title: 'Validación de posts', desc: 'Revisa que los cuatro posts sociales son realmente distintos entre sí y que cada uno funciona en su plataforma.' },
  { icon: '🔗', title: 'Alineación con el brief', desc: 'Verifica que todo lo que se generó respeta las instrucciones del Director. Las keywords están, el tono es el correcto, los CTAs no son agresivos.' },
];

const CHECKLIST = [
  'Tono consistente en todo el artículo',
  'Extensión suficiente (mínimo 810 palabras)',
  'Estructura lógica y fluida',
  'Posts únicos y adaptados a cada plataforma',
  'Keywords del Director presentes de forma natural',
  'Sin errores gramaticales ni frases vacías',
];

@customElement('agent-secretary-page')
export class AgentSecretaryPage extends LitElement {
  private goBack() {
    navigateBack('/arquitectura');
  }

  static styles = [
    pageFrameStyles,
    cardStyles,
    motionStyles,
    css`
      .back-btn {
        margin-bottom: var(--space-xl);
      }

      .agent-header {
        margin-bottom: var(--space-2xl);
      }

      .agent-emoji {
        font-size: 3rem;
        line-height: 1;
        margin-bottom: var(--space-md);
        display: block;
      }

      .agent-title {
        font-size: clamp(2rem, 5vw, 2.75rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        margin: 0 0 var(--space-sm);
        color: var(--color-text);
      }

      .agent-tagline {
        font-size: 1.125rem;
        color: var(--color-accent);
        font-weight: 500;
        margin: 0;
      }

      .section-title {
        font-size: 1.25rem;
        font-weight: 700;
        letter-spacing: -0.02em;
        margin: 0 0 var(--space-md);
        color: var(--color-text);
      }

      .what-section {
        margin-bottom: var(--space-2xl);
      }

      .what-section p {
        color: var(--color-text-muted);
        font-size: 1.0625rem;
        line-height: 1.7;
        margin: 0;
        max-width: 64ch;
      }

      .skills-grid {
        display: grid;
        gap: var(--space-md);
        grid-template-columns: 1fr;
        margin-bottom: var(--space-2xl);
      }

      @media (min-width: 640px) {
        .skills-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      .skill-card {
        display: flex;
        gap: var(--space-md);
        align-items: flex-start;
      }

      .skill-icon {
        font-size: 1.5rem;
        flex-shrink: 0;
        line-height: 1;
        margin-top: 0.1rem;
      }

      .skill-card h3 {
        margin: 0 0 0.25rem;
        font-size: 0.9375rem;
        font-weight: 600;
        color: var(--color-text);
      }

      .skill-card p {
        margin: 0;
        font-size: 0.875rem;
        color: var(--color-text-muted);
        line-height: 1.55;
      }

      .checklist-section {
        margin-bottom: var(--space-2xl);
      }

      .checklist {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        gap: var(--space-sm);
      }

      .checklist li {
        display: flex;
        align-items: center;
        gap: var(--space-sm);
        font-size: 0.9375rem;
        color: var(--color-text-muted);
        padding: var(--space-sm) var(--space-md);
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        border-radius: var(--radius);
      }

      .checklist li::before {
        content: '✓';
        color: var(--color-success, #10b981);
        font-weight: 700;
        font-size: 0.875rem;
        flex-shrink: 0;
      }

      .model-note {
        padding: var(--space-lg);
        border-radius: var(--radius);
        background: var(--gradient-primary-soft);
        border: 1px solid var(--color-border-glow);
        font-size: 0.9375rem;
        color: var(--color-text-muted);
        line-height: 1.6;
        margin-bottom: var(--space-xl);
      }

      .model-note strong {
        color: var(--color-text);
      }
    `,
  ];

  render() {
    return html`
      <div class="animate-fade-up">
        <div class="back-btn">
          <ui-button variant="ghost" @ui-click=${this.goBack}>← Volver</ui-button>
        </div>

        <div class="agent-header">
          <span class="agent-emoji">📋</span>
          <h1 class="agent-title">El Secretario</h1>
          <p class="agent-tagline">El auditor que decide si algo está listo para salir — o necesita otra vuelta.</p>
        </div>

        <div class="what-section">
          <h2 class="section-title">¿Qué hace?</h2>
          <p>
            El Secretario entra en escena cuando Writers han terminado. Su trabajo no es
            escribir, sino revisar con criterio: ¿el tono es coherente de principio a fin?,
            ¿el artículo tiene profundidad de verdad o se ha quedado corto?, ¿los posts suenan
            distintos o son el mismo mensaje con emojis diferentes? Si algo no pasa el corte,
            no lo devuelve — lo arregla él mismo. Solo cuando todo está bien, lo pasa al
            Director para la aprobación final.
          </p>
        </div>

        <div>
          <h2 class="section-title">Sus armas</h2>
          <div class="skills-grid">
            ${SKILLS.map(
              (s) => html`
                <article class="card skill-card">
                  <span class="skill-icon">${s.icon}</span>
                  <div>
                    <h3>${s.title}</h3>
                    <p>${s.desc}</p>
                  </div>
                </article>
              `,
            )}
          </div>
        </div>

        <div class="checklist-section">
          <h2 class="section-title">Su checklist</h2>
          <ul class="checklist">
            ${CHECKLIST.map((item) => html`<li>${item}</li>`)}
          </ul>
        </div>

        <div class="model-note">
          <strong>Modelo: Llama 3.3-70B</strong> — El Secretario ahora utiliza Llama 3.3-70B, capaz de mantener el contexto de piezas extensas y detectar matices sutiles en tono, voz y narrativa. No solo revisa en busca de errores o repeticiones: identifica profundidad real, coherencia entre ideas y construye un veredicto sólido sobre la calidad global del contenido. Gracias a este modelo puede argumentar mejoras, rematar textos con criterio y asegurar que todo lo que entrega esté a la altura del estándar Blunticle.
     
        </div>

        <ui-button href="https://blunticle.alejandrosn.com" variant="primary">Probar Blunticle</ui-button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agent-secretary-page': AgentSecretaryPage;
  }
}
